import { useEffect, useRef, useState } from "react";
import { StickyNote, ChevronDown, ChevronUp, Check, Zap } from "lucide-react";

// Quick-insert templates — tap = append a timestamped bullet to the note.
// Keep the list short & staff-relevant. Adjust freely without needing a backend change.
const NOTE_TEMPLATES = [
  "Called — no answer",
  "Called — left voicemail",
  "Spoke to client",
  "Waiting on drawings",
  "Chase email sent",
  "Booked survey",
  "Deposit promised",
];

// Floating "private staff note" pad shown ONLY when the office/admin is signed in and
// walking through /quote. Auto-persists to localStorage so context is never lost, then
// gets flushed to a proper quote note (POST /admin/quote/{qid}/note) the moment the
// quote is saved.
//
// - `value` / `onChange` are the LIVE draft string owned by the parent (Quote.jsx).
// - Parent takes care of clearing the draft after a successful save.
const LS_KEY = "tew_admin_note_draft";

export function StaffAdminNote({ value, onChange }) {
  const [open, setOpen] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        if (value) localStorage.setItem(LS_KEY, value);
        else localStorage.removeItem(LS_KEY);
        setSavedAt(Date.now());
      } catch { /* localStorage might be full/blocked — non-fatal */ }
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value]);

  const insertTemplate = (label) => {
    const stamp = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const line = `• ${stamp} · ${label}`;
    onChange((value ? value.replace(/\s+$/, "") + "\n" : "") + line + "\n");
  };

  const chars = value?.length || 0;

  return (
    <div data-testid="staff-admin-note" className="fixed z-40 bottom-4 right-4 w-[320px] max-w-[calc(100vw-2rem)] shadow-xl">
      <div className="bg-[#12263A] text-white flex items-center gap-2 px-3 py-2 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)} data-testid="staff-admin-note-header">
        <StickyNote size={14} className="text-[#C79A3B]" />
        <span className="text-[11px] uppercase tracking-widest font-bold">Private staff note</span>
        <span className="ml-auto flex items-center gap-2 text-[10px] text-white/60">
          {chars > 0 && savedAt && <span className="inline-flex items-center gap-1"><Check size={10} /> Saved</span>}
          {open ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </span>
      </div>
      {open && (
        <div className="bg-[#FFFDF7] border border-[#12263A] border-t-0 p-3">
          <div className="flex items-center gap-1 mb-2 text-[9px] uppercase tracking-widest text-[#6B7280] font-bold">
            <Zap size={10} className="fill-[#F59E0B] text-[#B45309]" /> Quick add
          </div>
          <div data-testid="staff-admin-note-templates" className="flex flex-wrap gap-1 mb-2">
            {NOTE_TEMPLATES.map(t => (
              <button key={t} type="button"
                data-testid={`staff-admin-note-tpl-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                onClick={() => insertTemplate(t)}
                className="text-[10px] font-bold text-[#12263A] bg-white border border-[#E5E7EB] px-2 py-1 hover:border-[#C79A3B] hover:bg-[#FFF8EE]">
                {t}
              </button>
            ))}
          </div>
          <textarea data-testid="staff-admin-note-textarea"
            value={value || ""} onChange={e => onChange(e.target.value)}
            rows={5}
            placeholder="Anything to remember about this client? (call notes, special asks, promises made…)&#10;&#10;Tap a template above for one-tap timestamped entries. Draft auto-saves and attaches to the quote on save."
            className="w-full text-xs leading-relaxed bg-transparent border border-[#E5E7EB] p-2 focus:outline-none focus:border-[#C79A3B] resize-none placeholder:text-[#9CA3AF]" />
          <div className="flex items-center justify-between mt-1 text-[10px] text-[#6B7280]">
            <span>{chars} char{chars === 1 ? "" : "s"} · draft auto-saves</span>
            {chars > 0 && (
              <button type="button" data-testid="staff-admin-note-clear" onClick={() => onChange("")}
                className="text-[#C0392B] font-bold hover:underline">Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Read the current draft from localStorage on load (used to seed parent state).
export function readAdminNoteDraft() {
  try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
}

// Clear the draft (used post-successful-save).
export function clearAdminNoteDraft() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
